import { useAnalytics } from '@capsicum/common';
import { useOnboardingAnalytics } from '@capsicum/socios-core';
import { useNCWalletCreation } from '@socios-blk/browser';
import {
	useNonCustodialProviderManagementAuthActionLogin,
	useNonCustodialProviderUserWalletStatus
} from '@socios-blk/core';
import { AppType, EventIdentifier, FeatureTeamEvent, TrackingEventType } from 'libs/common/models';
import { Logger } from 'libs/common/services';
import { type RecoveryKit } from 'libs/features/nc-onboarding/shared/src/models';
import { useState } from 'react';

import { ModalService } from '../../../../../common-web';
import { NCRegistrationConfirmDrawer, NCRegistrationErrorDrawer, NCRegistrationExistingUserDrawer } from '../ui';

/**
 * This hook is for Web Registration only
 */
export const useNCRegistrationWeb = (onSuccess: () => void) => {
	const [recoveryKit, setRecoveryKit] = useState<RecoveryKit>();
	const [isRegistrationStarted, setIsRegistrationStarted] = useState(false);

	const { setupNCWallet, setupSmartWallet } = useNCWalletCreation();
	const analytics = useAnalytics();
	const {
		sendRegistrationPasskeyCreationEvent,
		sendRegistrationPasskeyStartEvent,
		sendRegistrationRecoveryKitConfirmEvent
	} = useOnboardingAnalytics();

	const { data: authDfnsData } = useNonCustodialProviderManagementAuthActionLogin();
	const { isNCWalletRegistered, isSmartWalletRegistered, userDetails } = useNonCustodialProviderUserWalletStatus();

	const handleCaughtError = (error: Error) => {
		Logger.error(error as Error);

		analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
			extra_parameter: error.message,
			feature_team: FeatureTeamEvent.ORFT,
			text: 'Error during NC wallet registration process.'
		});

		sendRegistrationPasskeyCreationEvent('failure');

		openNCRegistrationErrorDrawer();
	};

	/**
	 * Open the error drawer for any issue during the registration process.
	 */
	const openNCRegistrationErrorDrawer = () => {
		setIsRegistrationStarted(false);

		ModalService.addModal({
			detached: false,
			renderComponent: ({ close }) => (
				<NCRegistrationErrorDrawer closeModal={close} testID="ncOnboarding-registrationError-drawer" />
			)
		});
	};

	/**
	 * Open the error drawer when a user already has both wallets.
	 */
	const openNCRegistrationExistingUserDrawer = () => {
		ModalService.addModal({
			detached: false,
			isClosable: false,
			renderComponent: ({ close }) => (
				<NCRegistrationExistingUserDrawer
					testID="ncOnboarding-registrationExistingUser-drawer"
					onConfirm={() => {
						onSuccess();
						close();
					}}
				/>
			)
		});
	};

	/**
	 * Open the confirmation drawer for the user to confirm the registration process after showing the recovery kit.
	 * When confirming, it will navigate the user to the nc-wallet screen.
	 */
	const openNCRegistrationConfirmDrawer = () => {
		ModalService.addModal({
			detached: false,
			renderComponent: ({ close }) => (
				<NCRegistrationConfirmDrawer
					closeModal={close}
					testID="ncOnboarding-registrationConfirm-drawer"
					onConfirm={() => {
						sendRegistrationRecoveryKitConfirmEvent();

						analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
							feature_team: FeatureTeamEvent.ORFT,
							text: 'user aknowledged storing his recovery kit and is taken to the NCW screen'
						});

						onSuccess();
						close();
					}}
				/>
			)
		});
	};

	/**
	 * Continues the registration flow from the biconomy smart wallet creation and registration phase.
	 *
	 * @param dfnsWalletId - The dfns wallet id
	 * @param dfnsWalletBlockchainAddress - The dfns wallet blockchain address
	 * @returns void
	 */
	const registerSmartWallet = async (
		dfnsWalletId: string,
		dfnsWalletBlockchainAddress: string,
		dfnsAuthToken: string
	) => {
		try {
			// If we do not have an auth token passed from the DFNS wallet creation, fetch a new one.
			const authToken = dfnsAuthToken ? dfnsAuthToken : authDfnsData?.token;

			if (!authToken) {
				throw new Error(
					'The auth token was not provided and could not be fetched from the DFNS wallet creation.'
				);
			}

			const { smartWalletRegistration } = await setupSmartWallet({
				appPlatform: AppType.webApp,
				dfnsAuthToken: authToken,
				dfnsWalletBlockchainAddress,
				dfnsWalletId
			});

			if (!smartWalletRegistration) {
				throw new Error(
					'Something may have gone wrong when registering the smart wallet with BE, as they did not return a sufficient receipt of registration.'
				);
			}
		} catch (error) {
			handleCaughtError(error as Error);
		}
	};

	/**
	 * Registers the NC wallet and smart wallet if neither are registered.
	 * Logs the process and updates the state with the recovery kit.
	 *
	 * @returns Promise<void>
	 */

	const handleNoWalletsRegistered = async () => {
		try {
			const { dfnsAuthToken, dfnsRecoveryKeyCode, dfnsRecoveryKeyId, dfnsWalletBlockchainAddress, dfnsWalletId } =
				await setupNCWallet();

			if (
				!dfnsWalletId ||
				!dfnsWalletBlockchainAddress ||
				!dfnsAuthToken ||
				!dfnsRecoveryKeyCode ||
				!dfnsRecoveryKeyId
			) {
				throw new Error(
					'setupNCWallet was not able to provide sufficient data to kick-start registerSmartWallet flow.'
				);
			}

			await registerSmartWallet(dfnsWalletId, dfnsWalletBlockchainAddress, dfnsAuthToken);

			setRecoveryKit({
				code: dfnsRecoveryKeyCode,
				keyId: dfnsRecoveryKeyId
			});

			analytics.sendEvent(TrackingEventType.track, EventIdentifier.SignUpNCWEnd, {
				feature_team: FeatureTeamEvent.ORFT,
				text: 'NCW wallet registration success when neither the NC wallet and the smart wallet exists.'
			});
		} catch (error) {
			handleCaughtError(error as Error);
		} finally {
			setIsRegistrationStarted(false);
		}
	};

	/**
	 * Registers the smart wallet if the NC wallet is registered but the smart wallet is not.
	 * Logs the process and calls onSuccess.
	 *
	 * @returns Promise<void>
	 */

	const handleNCWalletRegistered = async () => {
		try {
			await registerSmartWallet(
				userDetails?.nonCustodialWallet?.walletId || '',
				userDetails?.nonCustodialWallet?.blockchainAddress || '',
				''
			);

			analytics.sendEvent(TrackingEventType.track, EventIdentifier.SignUpNCWEnd, {
				feature_team: FeatureTeamEvent.ORFT,
				text: 'NCW wallet registration success when the NC wallet exists but the smart wallet not.'
			});

			onSuccess();
		} catch (error) {
			handleCaughtError(error as Error);
		}
	};

	/**
	 * Handles the click action which starts or continues non-custodial wallet registration process.
	 *
	 * If there is an error, it will open the error drawer.
	 * If there is no error, it will register the wallets and navigate the user to the nc-wallet screen.
	 *
	 * @returns void
	 */
	const startRegistration = () => {
		try {
			sendRegistrationPasskeyStartEvent();

			analytics.sendEvent(TrackingEventType.track, EventIdentifier.SignUpNCWStart, {
				feature_team: FeatureTeamEvent.ORFT,
				text: 'started NCW passkey registration process.'
			});

			setIsRegistrationStarted(true);

			if (!isNCWalletRegistered && !isSmartWalletRegistered) {
				handleNoWalletsRegistered();
			} else if (isNCWalletRegistered && !isSmartWalletRegistered) {
				handleNCWalletRegistered();
			}

			sendRegistrationPasskeyCreationEvent('success');
		} catch (error) {
			handleCaughtError(error as Error);
		}
	};

	return {
		isRegistrationStarted,
		openNCRegistrationConfirmDrawer,
		openNCRegistrationExistingUserDrawer,
		recoveryKit,
		startRegistration
	};
};
